import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { Icon, Button, toast } from '@tviklax/ui-components';
import styled from 'styled-components';
import type { OrderItem } from './types';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.gray100};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const TemplateLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const Dropzone = styled.div<{ $dragActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[8]};
  border: 2px dashed ${({ theme, $dragActive }) => ($dragActive ? theme.colors.primary : theme.colors.gray300)};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme, $dragActive }) => ($dragActive ? `${theme.colors.primary}08` : theme.colors.gray0 || '#fff')};
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  text-align: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const StatusBanner = styled.div<{ $variant: 'error' | 'success' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: 14px;
  font-weight: 500;
  background: ${({ $variant }) => ($variant === 'error' ? '#fef2f2' : '#ecfdf3')};
  border: 1px solid ${({ $variant }) => ($variant === 'error' ? '#fecaca' : '#bbf7d0')};
  color: ${({ $variant }) => ($variant === 'error' ? '#991b1b' : '#166534')};
`;

const GridScroll = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const Grid = styled.table`
  min-width: 900px;
  width: 100%;
  border-collapse: collapse;
`;

const GridHead = styled.thead`
  background: ${({ theme }) => theme.colors.gray100};
`;

const GridHeaderCell = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
`;

const GridRow = styled.tr`
  border-top: 1px solid ${({ theme }) => theme.colors.gray100};
`;

const GridCell = styled.td<{ $invalid?: boolean }>`
  padding: 6px 8px;
  background: ${({ $invalid }) => ($invalid ? '#fee2e2' : 'transparent')};
`;

const CellInput = styled.input<{ $invalid?: boolean }>`
  width: 100%;
  min-width: 90px;
  padding: 6px 8px;
  border: 1px solid ${({ $invalid, theme }) => ($invalid ? '#ef4444' : theme.colors.gray200)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  background: transparent;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

interface BulkRow {
  id: number;
  description: string;
  hsCode: string;
  quantity: string;
  unitPrice: string;
  countryOfOrigin: string;
}

const TEMPLATE_HEADERS = ['description', 'hsCode', 'quantity', 'unitPrice', 'countryOfOrigin'];

const REQUIRED_COLUMN_ALIASES: Record<string, string[]> = {
  description: ['description', 'item description', 'product description', 'product'],
  hsCode: ['hscode', 'hs code', 'hsn', 'hsn code', 'hs/hsn code'],
  quantity: ['quantity', 'qty'],
  unitPrice: ['unitprice', 'unit price', 'price'],
  countryOfOrigin: ['countryoforigin', 'country of origin', 'origin'],
};

const normalizeHeader = (value: string) => value.trim().toLowerCase();

const buildColumnMap = (headerRow: string[]): Record<string, number> => {
  const map: Record<string, number> = {};
  headerRow.forEach((raw, index) => {
    const normalized = normalizeHeader(raw);
    Object.entries(REQUIRED_COLUMN_ALIASES).forEach(([field, aliases]) => {
      if (aliases.includes(normalized) && map[field] === undefined) {
        map[field] = index;
      }
    });
  });
  return map;
};

// Small hand-rolled CSV line parser — good enough for the flat, quoted-field
// manifests this template produces without pulling in a parsing dependency
// (xlsx is already needed for real spreadsheet support, CSV doesn't need it).
const parseCsvText = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
};

const rowsFromSheet = (sheet: string[][]): BulkRow[] => {
  if (sheet.length === 0) return [];
  const [header, ...body] = sheet;
  const columnMap = buildColumnMap(header);

  return body.map((cells, index) => ({
    id: index,
    description: columnMap.description !== undefined ? (cells[columnMap.description] || '').trim() : '',
    hsCode: columnMap.hsCode !== undefined ? (cells[columnMap.hsCode] || '').trim() : '',
    quantity: columnMap.quantity !== undefined ? (cells[columnMap.quantity] || '').trim() : '',
    unitPrice: columnMap.unitPrice !== undefined ? (cells[columnMap.unitPrice] || '').trim() : '',
    countryOfOrigin: columnMap.countryOfOrigin !== undefined ? (cells[columnMap.countryOfOrigin] || '').trim() : '',
  }));
};

const isValidHsCode = (value: string) => /^\d{4,10}$/.test(value.trim());
const isValidQuantity = (value: string) => /^\d+(\.\d+)?$/.test(value.trim()) && Number(value) > 0;
const isValidUnitPrice = (value: string) => /^\d+(\.\d+)?$/.test(value.trim()) && Number(value) >= 0;

interface RowErrors {
  description?: boolean;
  hsCode?: boolean;
  quantity?: boolean;
  unitPrice?: boolean;
}

const validateRow = (row: BulkRow): RowErrors => {
  const errors: RowErrors = {};
  if (!row.description.trim()) errors.description = true;
  if (!isValidHsCode(row.hsCode)) errors.hsCode = true;
  if (!isValidQuantity(row.quantity)) errors.quantity = true;
  if (!isValidUnitPrice(row.unitPrice)) errors.unitPrice = true;
  return errors;
};

const downloadTemplate = () => {
  const csv = `${TEMPLATE_HEADERS.join(',')}\nCotton T-Shirts,610910,500,4.25,India\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'tvikla-bulk-order-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface BulkUploadProps {
  onItemsConfirmed: (items: OrderItem[]) => void;
}

export const BulkUpload: React.FC<BulkUploadProps> = ({ onItemsConfirmed }) => {
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);

  const loadSheetRows = useCallback((sheet: string[][], name: string) => {
    const parsedRows = rowsFromSheet(sheet);
    if (parsedRows.length === 0) {
      toast({
        title: 'No rows found',
        description: 'The file was read but no data rows were detected. Check it matches the template columns.',
        variant: 'destructive',
      });
      return;
    }
    setRows(parsedRows);
    setFileName(name);
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.name.toLowerCase().endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = () => {
        const text = String(reader.result || '');
        loadSheetRows(parseCsvText(text), file.name);
      };
      reader.readAsText(file);
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        const workbook = XLSX.read(reader.result, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const asRows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
        loadSheetRows(asRows.map((r) => r.map((cell) => String(cell ?? ''))), file.name);
      };
      reader.readAsArrayBuffer(file);
    }
  }, [loadSheetRows]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const rowErrors = rows.map(validateRow);
  const invalidCount = rowErrors.filter((e) => Object.keys(e).length > 0).length;

  const updateCell = (id: number, field: keyof BulkRow, value: string) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const removeRow = (id: number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleConfirm = () => {
    if (invalidCount > 0) {
      toast({
        title: 'Fix invalid rows first',
        description: `${invalidCount} row${invalidCount === 1 ? '' : 's'} still ${invalidCount === 1 ? 'has' : 'have'} missing HS codes or invalid quantity/price formats.`,
        variant: 'destructive',
      });
      return;
    }

    const items: OrderItem[] = rows.map((row, index) => {
      const quantity = Number(row.quantity);
      const unitPrice = Number(row.unitPrice);
      const lineTotal = Number((quantity * unitPrice).toFixed(2));
      return {
        itemno: index + 1,
        productId: '',
        productName: row.description,
        details: row.description,
        unit: 'pcs',
        quantity,
        unitPrice,
        price: unitPrice,
        total: lineTotal,
        subtotal: lineTotal,
        hsCode: row.hsCode,
        countryOfOrigin: row.countryOfOrigin,
        exportControlFlag: '',
        ecgcEligibility: 'Pending check',
      };
    });

    toast({
      title: 'Manifest ingested',
      description: `${items.length} line item${items.length === 1 ? '' : 's'} added — continue the order in the manual workflow.`,
    });
    onItemsConfirmed(items);
  };

  return (
    <Wrapper>
      <TemplateRow>
        <div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>Bulk shipment manifest</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Upload a CSV or XLSX file with one row per line item.
          </div>
        </div>
        <TemplateLink type="button" onClick={downloadTemplate}>
          <Icon name="Download" size={16} />
          Download CSV Schema Template
        </TemplateLink>
      </TemplateRow>

      <Dropzone {...getRootProps()} $dragActive={isDragActive}>
        <input {...getInputProps()} />
        <Icon name="Upload" size={32} color="#f86400" />
        <div>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
            {isDragActive ? 'Drop the manifest here' : fileName ? `Loaded: ${fileName}` : 'Drop CSV/XLSX manifest here'}
          </h3>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '4px' }}>
            or click to browse — matches the downloadable template columns
          </p>
        </div>
        <Button variant="outline" size="sm" type="button">Select File</Button>
      </Dropzone>

      {rows.length > 0 && (
        <>
          <StatusBanner $variant={invalidCount > 0 ? 'error' : 'success'}>
            <Icon name={invalidCount > 0 ? 'AlertTriangle' : 'CheckCircle'} size={18} />
            {invalidCount > 0
              ? `Error: ${invalidCount} row${invalidCount === 1 ? '' : 's'} contain missing HS codes or invalid quantity/price formats. Fix the highlighted cells below.`
              : `All ${rows.length} rows look valid — ready to add to the order.`}
          </StatusBanner>

          <GridScroll>
            <Grid>
              <GridHead>
                <tr>
                  <GridHeaderCell>Description</GridHeaderCell>
                  <GridHeaderCell>HS Code</GridHeaderCell>
                  <GridHeaderCell>Quantity</GridHeaderCell>
                  <GridHeaderCell>Unit Price</GridHeaderCell>
                  <GridHeaderCell>Country of Origin</GridHeaderCell>
                  <GridHeaderCell aria-label="Remove row" />
                </tr>
              </GridHead>
              <tbody>
                {rows.map((row, index) => {
                  const errors = rowErrors[index];
                  return (
                    <GridRow key={row.id}>
                      <GridCell $invalid={errors.description}>
                        <CellInput
                          $invalid={errors.description}
                          value={row.description}
                          onChange={(e) => updateCell(row.id, 'description', e.target.value)}
                        />
                      </GridCell>
                      <GridCell $invalid={errors.hsCode}>
                        <CellInput
                          $invalid={errors.hsCode}
                          value={row.hsCode}
                          placeholder="e.g. 610910"
                          onChange={(e) => updateCell(row.id, 'hsCode', e.target.value)}
                        />
                      </GridCell>
                      <GridCell $invalid={errors.quantity}>
                        <CellInput
                          $invalid={errors.quantity}
                          value={row.quantity}
                          onChange={(e) => updateCell(row.id, 'quantity', e.target.value)}
                        />
                      </GridCell>
                      <GridCell $invalid={errors.unitPrice}>
                        <CellInput
                          $invalid={errors.unitPrice}
                          value={row.unitPrice}
                          onChange={(e) => updateCell(row.id, 'unitPrice', e.target.value)}
                        />
                      </GridCell>
                      <GridCell>
                        <CellInput
                          value={row.countryOfOrigin}
                          onChange={(e) => updateCell(row.id, 'countryOfOrigin', e.target.value)}
                        />
                      </GridCell>
                      <GridCell>
                        <Button variant="ghost" size="sm" onClick={() => removeRow(row.id)}>
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </GridCell>
                    </GridRow>
                  );
                })}
              </tbody>
            </Grid>
          </GridScroll>

          <RowActions>
            <Button variant="outline" onClick={() => { setRows([]); setFileName(null); }}>
              Start Over
            </Button>
            <Button onClick={handleConfirm} disabled={invalidCount > 0}>
              {invalidCount > 0
                ? `Fix ${invalidCount} Row${invalidCount === 1 ? '' : 's'} to Continue`
                : `Add ${rows.length} Item${rows.length === 1 ? '' : 's'} to Order`}
            </Button>
          </RowActions>
        </>
      )}
    </Wrapper>
  );
};
